import { useCallback, useEffect, useRef, useState } from 'react'
import { type SetValueConfig, useWatch } from 'react-hook-form'

import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { useLanguage } from '~/common/hooks'
import type { ShopeeLogisticsProviderInput } from '~/common/schemas/logistics-shopee'
import { CHANNEL_ID, CURRENCY_SYMBOLS } from '~/consts'
import { useChannelLogisticsQuery } from '~/products/hooks/useChannelLogisticsQuery'
import { useProductFormContext } from '../../hooks/useProductForm'

const defaultValue = {
    channel: 'shopee' as const,
    value: [] as ShopeeLogisticsProviderInput[],
}

function isValidCountryCode(code: unknown): code is keyof typeof CURRENCY_SYMBOLS {
    return typeof code === 'string' && code in CURRENCY_SYMBOLS
}

type Props = {
    shopId: number
    countryCode: string | null
}

export function ShopeeLogisticsProviders({ shopId, countryCode }: Props) {
    const { data: providersData, status } = useChannelLogisticsQuery({ channel: 'shopee', shopId })
    const { control, getValues, setValue } = useProductFormContext()
    const logisticsAttribute = useWatch({
        control,
        name: `channel_attribute_values.${CHANNEL_ID.Shopee}.product.logistics.value.shop-${shopId}`,
        defaultValue: { ...defaultValue },
    })

    const language = useLanguage() as 'th' | 'vn'
    const validCountryCode = isValidCountryCode(countryCode) ? countryCode : language
    const [infoDialogOpen, setInfoDialogOpen] = useState(false)
    const [editableSelectedProvider, setEditableSelectedProvider] =
        useState<ShopeeLogisticsProviderInput | null>(null)

    const openDialog = useCallback(
        ({ logisticId, fromExisting = false }: { logisticId: number; fromExisting?: boolean }) => {
            if (logisticsAttribute.channel !== 'shopee') {
                return
            }

            const lookupData = fromExisting ? logisticsAttribute.value : providersData
            const provider: ShopeeLogisticsProviderInput | undefined = lookupData?.find(
                (p) => p.logistic_id === logisticId
            )
            if (!provider) {
                return
            }

            setInfoDialogOpen(true)
            setEditableSelectedProvider({ ...provider, enabled: provider.enabled ?? true })
        },
        [providersData, logisticsAttribute]
    )

    const getCurrentLogisticsAttribute = useCallback(() => {
        const attribute = getValues(
            `channel_attribute_values.${CHANNEL_ID.Shopee}.product.logistics.value.shop-${shopId}`
        ) ?? { ...defaultValue }

        if (attribute.channel !== 'shopee') {
            return
        }

        return attribute
    }, [shopId, getValues])

    const setCurrentLogisticsAttribute = useCallback(
        (
            logisticsAttribute: NonNullable<ReturnType<typeof getCurrentLogisticsAttribute>>,
            options: Partial<SetValueConfig> = {}
        ) => {
            setValue(
                `channel_attribute_values.${CHANNEL_ID.Shopee}.product.logistics.value.shop-${shopId}`,
                { ...logisticsAttribute },
                { ...options }
            )
        },
        [shopId, setValue]
    )

    const hasSyncedLogisticsProviders = useRef(false)
    useEffect(() => {
        if (!providersData || hasSyncedLogisticsProviders.current) {
            return
        }

        const currentLogisticsAttribute = getCurrentLogisticsAttribute()
        if (!currentLogisticsAttribute?.value?.length) {
            hasSyncedLogisticsProviders.current = true
            return
        }

        const validLogisticIds = new Set(providersData.map((provider) => provider.logistic_id))
        const validAttributeValues = currentLogisticsAttribute.value.filter((provider) =>
            validLogisticIds.has(provider.logistic_id)
        )

        const requiresSync = validAttributeValues.length < currentLogisticsAttribute.value.length
        if (requiresSync) {
            setCurrentLogisticsAttribute({
                ...currentLogisticsAttribute,
                value: validAttributeValues,
            })
        }

        hasSyncedLogisticsProviders.current = true
    }, [providersData, getCurrentLogisticsAttribute, setCurrentLogisticsAttribute])

    const applyChanges = useCallback(() => {
        const currentLogisticsAttribute = getCurrentLogisticsAttribute()

        if (!editableSelectedProvider || !currentLogisticsAttribute) {
            return
        }

        const logisticsAttributeValue = currentLogisticsAttribute.value
        const existingProvider = logisticsAttributeValue.find(
            (p) => p.logistic_id === editableSelectedProvider.logistic_id
        )

        const nextValue = existingProvider
            ? logisticsAttributeValue.map((p) =>
                  p.logistic_id === editableSelectedProvider.logistic_id
                      ? { ...p, ...editableSelectedProvider }
                      : p
              )
            : [...logisticsAttributeValue, editableSelectedProvider]

        setCurrentLogisticsAttribute({ ...currentLogisticsAttribute, value: nextValue })
        setInfoDialogOpen(false)
    }, [editableSelectedProvider, getCurrentLogisticsAttribute, setCurrentLogisticsAttribute])

    const toggleProvider = useCallback(
        (logisticId: number, enabled: boolean) => {
            const currentLogisticsAttribute = getCurrentLogisticsAttribute()
            const providerData = providersData?.find((p) => p.logistic_id === logisticId)

            if (!providerData || !currentLogisticsAttribute) {
                return
            }

            const logisticsAttributeValue = currentLogisticsAttribute.value
            const needsMoreInfo =
                enabled && providerData.fee_type === 'CUSTOM_PRICE' && !providerData.shipping_fee
            if (needsMoreInfo) {
                openDialog({ logisticId: providerData.logistic_id })
                return
            }

            const hasExistingValue = logisticsAttributeValue.some(
                (p) => p.logistic_id === providerData.logistic_id
            )

            const nextValue = hasExistingValue
                ? logisticsAttributeValue.map((p) => {
                      return p.logistic_id === providerData.logistic_id ? { ...p, enabled } : p
                  })
                : [...logisticsAttributeValue, { ...providerData, enabled }]

            setCurrentLogisticsAttribute(
                { ...currentLogisticsAttribute, value: nextValue },
                { shouldValidate: true }
            )
        },
        [providersData, openDialog, getCurrentLogisticsAttribute, setCurrentLogisticsAttribute]
    )

    if (status === 'loading') {
        return <Typography variant="body2">{gettext('Loading...')}</Typography>
    }

    if (status === 'error') {
        return (
            <Typography color="error" variant="subtitle2">
                {gettext('Failed to load shipping providers')}
            </Typography>
        )
    }

    if (logisticsAttribute.channel !== 'shopee') {
        return null
    }

    const logisticsAttributeValue = logisticsAttribute.value
    const enabledLogisticsProviders = new Set(
        logisticsAttributeValue.filter((v) => v.enabled).map((v) => v.logistic_id)
    )

    return (
        <>
            <Stack gap={0.5}>
                <Typography
                    variant="body2"
                    sx={{ visibility: enabledLogisticsProviders.size > 0 ? 'hidden' : 'visible' }}
                >
                    {gettext('No shipping options enabled. You must enable at least one.')}
                </Typography>

                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        padding: 2,
                    }}
                >
                    {providersData.map((provider) => (
                        <LogisticProvider
                            key={provider.logistic_id}
                            provider={provider}
                            existingProvider={logisticsAttributeValue.find(
                                (p) => p.enabled && p.logistic_id === provider.logistic_id
                            )}
                            countryCode={validCountryCode}
                            enabled={enabledLogisticsProviders.has(provider.logistic_id)}
                            onToggle={toggleProvider}
                            onEdit={(logisticId) => {
                                openDialog({ logisticId, fromExisting: true })
                            }}
                        />
                    ))}
                </Box>
            </Stack>

            {editableSelectedProvider && (
                <Dialog
                    open={infoDialogOpen}
                    TransitionProps={{ onExited: () => setEditableSelectedProvider(null) }}
                    onClose={() => setInfoDialogOpen(false)}
                >
                    <DialogTitle>{editableSelectedProvider.logistic_name}</DialogTitle>
                    <DialogContent>
                        <Stack direction="row" gap={1}>
                            <TextField
                                type="number"
                                size="small"
                                margin="dense"
                                variant="standard"
                                value={editableSelectedProvider.shipping_fee ?? '0'}
                                disabled={editableSelectedProvider.is_free}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {CURRENCY_SYMBOLS[validCountryCode]}
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{ min: 0 }}
                                onChange={(e) => {
                                    setEditableSelectedProvider({
                                        ...editableSelectedProvider,
                                        shipping_fee: parseFloat(e.target.value),
                                    })
                                }}
                            />
                            <FormControlLabel
                                label={gettext('I will cover the shipping fee')}
                                control={
                                    <Checkbox
                                        checked={editableSelectedProvider.is_free}
                                        color="secondary"
                                        onChange={(_, checked) => {
                                            setEditableSelectedProvider({
                                                ...editableSelectedProvider,
                                                shipping_fee: checked ? undefined : 0,
                                                is_free: checked,
                                            })
                                        }}
                                    />
                                }
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ color: 'text.secondary' }}>
                        <Button color="inherit" onClick={() => setInfoDialogOpen(false)}>
                            {gettext('Cancel')}
                        </Button>
                        <Button autoFocus color="secondary" onClick={applyChanges}>
                            {gettext('Apply & Enable')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    )
}

type ExistingProvider = Pick<
    ShopeeLogisticsProviderInput,
    'logistic_id' | 'fee_type' | 'shipping_fee' | 'weight_limits'
>

type LogisticProviderProps = {
    provider: Pick<ShopeeLogisticsProviderInput, 'logistic_id' | 'logistic_name'>
    existingProvider?: ExistingProvider
    countryCode: keyof typeof CURRENCY_SYMBOLS
    enabled: boolean
    onToggle: (logisticId: number, checked: boolean) => void
    onEdit: (logisticId: number) => void
}

function LogisticProvider({
    provider,
    existingProvider,
    countryCode,
    enabled,
    onToggle,
    onEdit,
}: LogisticProviderProps) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap={1}
        >
            <Typography variant="body2">{provider.logistic_name}</Typography>
            <Box sx={{ flexShrink: 0, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {existingProvider && (
                    <LogisticProviderAction
                        provider={existingProvider}
                        countryCode={countryCode}
                        onEdit={onEdit}
                    />
                )}
                <Switch
                    checked={enabled}
                    color="secondary"
                    onChange={(_, checked) => onToggle(provider.logistic_id, checked)}
                />
            </Box>
        </Stack>
    )
}

type LogisticProviderActionProps = {
    provider: ExistingProvider
    countryCode: keyof typeof CURRENCY_SYMBOLS
    onEdit: (logisticId: number) => void
}

function LogisticProviderAction({ provider, countryCode, onEdit }: LogisticProviderActionProps) {
    const { control } = useProductFormContext()
    const packageWeight = useWatch({ control, name: 'package_weight' })

    switch (provider.fee_type) {
        case 'CUSTOM_PRICE': {
            if (typeof provider.shipping_fee === 'undefined') {
                return null
            }

            return (
                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="body2">
                        {CURRENCY_SYMBOLS[countryCode] + provider.shipping_fee}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="Edit"
                        onClick={() => {
                            if (provider.logistic_id) {
                                onEdit(provider.logistic_id)
                            }
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                </Stack>
            )
        }

        case 'SIZE_INPUT': {
            if (!packageWeight) {
                return (
                    <Typography color="error" variant="subtitle2">
                        {gettext('Requires weight')}
                    </Typography>
                )
            }

            if (!provider.weight_limits) {
                return null
            }

            return packageWeight > provider.weight_limits.item_max_weight ? (
                <Typography color="error" variant="subtitle2">
                    {gettext('Overweight')}
                </Typography>
            ) : null
        }

        default:
            return null
    }
}
